import { useEffect, useRef, useState, useCallback } from 'react';
import api from '../utils/api';

const SEND_INTERVAL = 90000; // 90 seconds
const STALE_THRESHOLD = 180000; // 3 minutes

export default function DriverLocationTracker({ user }) {
  const [status, setStatus] = useState('initializing'); // initializing | active | lost | denied
  const [lastSent, setLastSent] = useState(null);
  const watchRef = useRef(null);
  const intervalRef = useRef(null);
  const latestCoords = useRef(null);
  const swRef = useRef(null);

  // Register Service Worker + Periodic Background Sync
  const registerSW = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return;
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      swRef.current = reg;
      console.log('[Location] SW registered');

      // Send config to SW (API URL + auth token)
      const apiUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const token = localStorage.getItem('token');
      if (reg.active) {
        reg.active.postMessage({
          type: 'SET_CONFIG',
          payload: { apiUrl, token },
        });
      }
      // Wait for SW to activate if installing
      reg.addEventListener('updatefound', () => {
        const sw = reg.installing;
        sw.addEventListener('statechange', () => {
          if (sw.state === 'activated') {
            sw.postMessage({ type: 'SET_CONFIG', payload: { apiUrl, token } });
          }
        });
      });

      // Register periodic background sync (Chrome PWA only, ~15min minimum)
      if ('periodicSync' in reg) {
        try {
          const perms = await navigator.permissions.query({ name: 'periodic-background-sync' });
          if (perms.state === 'granted') {
            await reg.periodicSync.register('driver-location-periodic', {
              minInterval: 5 * 60 * 1000, // 5 minutes (Chrome enforces minimum)
            });
            console.log('[Location] Periodic sync registered');
          }
        } catch {
          // Periodic sync not supported — fine, we have foreground fallback
        }
      }
    } catch (e) {
      console.error('[Location] SW registration failed:', e);
    }
  }, []);

  // Send coords both directly (API) and to SW (queue for offline/background)
  const sendCoords = useCallback(async () => {
    if (!latestCoords.current) return;
    const coords = latestCoords.current;

    // Send to SW queue (for background sync if tab closes)
    if (swRef.current?.active) {
      swRef.current.active.postMessage({
        type: 'QUEUE_LOCATION',
        payload: coords,
      });
    }

    // Also send directly via API
    try {
      await api.post('/routeops/drivers/update-location', coords);
      setLastSent(new Date());
      setStatus('active');
    } catch {
      // SW queue will handle retry
    }
  }, []);

  // Listen for SW requesting fresh location
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === 'REQUEST_LOCATION' && latestCoords.current) {
        sendCoords();
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [sendCoords]);

  useEffect(() => {
    if (!user || user.role !== 'Driver') return;

    // Register SW for background sync
    registerSW();

    if (!navigator.geolocation) {
      setStatus('denied');
      return;
    }

    const onSuccess = (pos) => {
      latestCoords.current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      setStatus('active');
    };

    const onError = (err) => {
      console.error('Geolocation error:', err);
      if (err.code === 1) setStatus('denied');
      else setStatus('lost');
    };

    watchRef.current = navigator.geolocation.watchPosition(onSuccess, onError, {
      enableHighAccuracy: true,
      maximumAge: 60000,
      timeout: 30000,
    });

    // Send immediately once, then every SEND_INTERVAL
    const initTimer = setTimeout(sendCoords, 3000);
    intervalRef.current = setInterval(sendCoords, SEND_INTERVAL);

    // Warn on tab close
    const handleBeforeUnload = (e) => {
      // Queue one last location update to SW before closing
      if (latestCoords.current && swRef.current?.active) {
        swRef.current.active.postMessage({
          type: 'QUEUE_LOCATION',
          payload: latestCoords.current,
        });
      }
      e.preventDefault();
      e.returnValue = 'Closing this tab will disable location tracking. Are you sure?';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Stale signal detector
    const staleChecker = setInterval(() => {
      if (lastSent && Date.now() - lastSent.getTime() > STALE_THRESHOLD) {
        setStatus('lost');
      }
    }, 30000);

    return () => {
      if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
      clearTimeout(initTimer);
      clearInterval(intervalRef.current);
      clearInterval(staleChecker);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [user, registerSW, sendCoords]);

  // Don't render for non-drivers
  if (!user || user.role !== 'Driver') return null;

  // Full-screen blocker if GPS denied
  if (status === 'denied') {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6" style={{ background: 'var(--bg-void)' }}>
        <div className="text-center max-w-md space-y-4">
          <div className="text-5xl">&#x26A0;</div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fd)' }}>Location Access Required</h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Location access is required to use this device. Please enable location in your browser settings and refresh.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-lg font-bold uppercase text-sm"
            style={{ background: 'var(--amber)', color: 'var(--bg-void)', fontFamily: 'var(--fd)' }}
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  // Non-dismissable status pill
  return (
    <div
      className="fixed bottom-4 left-4 z-[100] flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg"
      style={{
        background: status === 'active' ? 'rgba(46,204,113,0.15)' : 'rgba(231,76,60,0.15)',
        border: `1px solid ${status === 'active' ? 'rgba(46,204,113,0.4)' : 'rgba(231,76,60,0.4)'}`,
        color: status === 'active' ? 'var(--green)' : 'var(--red)',
        fontFamily: 'var(--fm)',
      }}
      data-testid="driver-location-pill"
    >
      <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: status === 'active' ? 'var(--green)' : 'var(--red)' }} />
      {status === 'active' ? 'Location: Active' : status === 'lost' ? 'Location: Signal Lost' : 'Location: Connecting...'}
    </div>
  );
}
